// Copyright © 2024 Move Closer
var TeaserBoxAddonsMixin_1;
import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import { ListType, TextListWithAddons, ThumbnailsListWithAddons, Tile, TileSize, TileSizeMobile } from '../_components/ui';
import { ContentType, ModuleAdditionalRows } from '../_contracts';
import { TeaserBoxInnerGrid } from './TeaserBox.contracts';
import { addonsRegistry } from './TeaserBox.config';
import { InnerGridWrapper } from './_partials';
/**
 * Base mixin for TeaserBox components with addon support
 * Provides common functionality for handling addons and merged item lists
 */
let TeaserBoxAddonsMixin = TeaserBoxAddonsMixin_1 = class TeaserBoxAddonsMixin extends TeaserBoxInnerGrid {
    constructor() {
        super(...arguments);
        this.listComponent = null;
        this.tiles = [];
        this.gridConfig = {
            defaultSize: TileSize.S,
            mobileSize: TileSizeMobile.TILE_PLUS_TEXT,
            tiles: {
                0: TileSize.L
            },
            listOffset: 5,
            listType: ListType.LIST
        };
        this.TileSize = TileSize;
    }
    get moduleVersion() {
        return this.constructor.moduleVersion;
    }
    created() {
        this.tiles = this.getTilesAndAddons();
        if (this.gridConfig.listType !== false) {
            this.listComponent = this.combineToListComponent();
        }
    }
    get tilesOffset() {
        return this.gridConfig.listOffset;
    }
    /**
     * Common grid style getter for text list layouts
     */
    get gridStyle() {
        switch (this.teaserBoxConfig.additionalRows) {
            case ModuleAdditionalRows.None:
                return 'teaser-box__list-wrapper';
            case ModuleAdditionalRows.addOne:
                return 'teaser-box__list-wrapper-three-rows';
            case ModuleAdditionalRows.addTwo:
                return 'teaser-box__list-wrapper-four-rows';
            default:
                return 'teaser-box__list-wrapper';
        }
    }
    /**
     * Gets component type for merged items - shared logic across all components
     */
    getComponentType(item) {
        switch (item.tileType) {
            case TileSize.L:
            case TileSize.S:
            case TileSize.XL:
                return 'Tile';
            case 'addon':
                return addonsRegistry[item.data.driver] || 'div';
            default:
                return 'div';
        }
    }
    getListComponent(type) {
        switch (type) {
            case 'thumbnails':
                return 'ThumbnailsListWithAddons';
            case 'list':
            default:
                return 'TextListWithAddons';
        }
    }
    /**
     * Gets component props for merged items - shared logic with extension points
     */
    getComponentProps(item) {
        switch (item.tileType) {
            case TileSize.S:
                return this.getTileSmallProps(item);
            case TileSize.L:
                return this.getTileLargeProps(item);
            case TileSize.XL:
                return this.getTileXLargeProps(item);
            case ListType.LIST:
                return this.getListsProps(item);
            case 'addon':
                return {
                    module: item.data,
                    ...this.getAddonProps(item)
                };
            default:
                return {};
        }
    }
    /**
     * Extension point for large tile props
     */
    getTileLargeProps(item) {
        return {
            ...this.tileConfig[TileSize.L],
            article: item.data,
            fallbackImage: this.fallbackImage,
            windowService: this.windowService,
            isLazy: this.isLazy,
            isMobile: this.isMobile
        };
    }
    /**
     * Extension point for small tile props
     */
    getTileSmallProps(item) {
        return {
            ...this.tileConfig[TileSize.S],
            article: item.data,
            fallbackImage: this.fallbackImage,
            windowService: this.windowService,
            isLazy: this.isLazy,
            isMobile: this.isMobile,
            titleOnImage: true
        };
    }
    /**
     * Extension point for extra large tile props
     */
    getTileXLargeProps(item) {
        return {
            ...this.tileConfig[TileSize.XL],
            article: item.data,
            fallbackImage: this.fallbackImage,
            windowService: this.windowService,
            isLazy: this.isLazy,
            isMobile: this.isMobile
        };
    }
    /**
     * Extension point for thumbnails list with addons props
     */
    getListsProps(item) {
        return {
            ...this.listConfig[this.gridConfig.listType],
            items: item.data,
            fallbackImage: this.fallbackImage,
            windowService: this.windowService,
            listConfig: this.teaserBoxConfig,
            readMoreLink: this.readMoreLink,
            isLazy: this.isLazy,
            addonProps: this.getAddonProps(item)
        };
    }
    /**
     * Extension point for addon props (TeaserBox version with services)
     */
    getAddonProps(item) {
        // Handle case where addon might be an array instead of single module
        const module = Array.isArray(item.data) ? item.data[0] : item.data;
        return {
            module,
            eventBus: this.eventBus,
            relatedService: this.relatedService,
            shareService: this.shareService,
            windowService: this.windowService,
            theme: this.theme,
            fallbackImage: this.fallbackImage,
            isMobile: this.isMobile,
            onEmptyCallback: this.onEmptyCallback,
            pdfService: null,
            forceHide: false
        };
    }
    /**
     * Gets tiles and addons for the first part of the layout
     */
    getTilesAndAddons() {
        return this.items.slice(0, this.tilesOffset).map(item => {
            if ((item.type === ContentType.Article || item.type === ContentType.LiveArticle) && this.gridConfig.tiles[item.position]) {
                return { ...item, tileType: this.gridConfig.tiles[item.position] };
            }
            return { ...item };
        });
    }
    /**
     * Gets text list and addons for the second part of the layout
     */
    combineToListComponent() {
        const items = this.items.slice(this.tilesOffset);
        const listType = this.gridConfig.listType !== false ? this.gridConfig.listType : ListType.LIST;
        return {
            type: 'component',
            tileType: ListType.LIST,
            component: this.getListComponent(listType),
            data: items,
            props: this.$props,
            position: this.tilesOffset,
            key: 'list'
        };
    }
    get listConfig() {
        return {
            [ListType.THUMBNAILS]: {
                showArticleTags: this.teaserBoxConfig.showArticleTags,
                titleVersion: this.teaserBoxConfig.titleVersion,
                tile: {
                    isPremium: this.teaserBoxConfig.isPremium,
                    showAuthorAvatar: this.teaserBoxConfig.showAuthorAvatar,
                    showAuthorName: this.teaserBoxConfig.showAuthorName,
                    showCategory: this.teaserBoxConfig.showCategory,
                    showPublicationDate: this.teaserBoxConfig.showPublicationDate,
                    showTitle: true,
                    size: TileSize.S,
                    mobileSize: this.gridConfig.mobileSize,
                    titleOnImage: true,
                    gradientColor: this.teaserBoxConfig.gradientColor,
                    showArticleTags: this.teaserBoxConfig.showArticleTags
                }
            },
            [ListType.LIST]: {
                showArticleTags: this.teaserBoxConfig.showArticleTags,
                titleVersion: this.teaserBoxConfig.titleVersion,
                tile: {
                    isPremium: this.teaserBoxConfig.isPremium,
                    showAuthorAvatar: this.teaserBoxConfig.showAuthorAvatar,
                    showAuthorName: this.teaserBoxConfig.showAuthorName,
                    showCategory: this.teaserBoxConfig.showCategory,
                    showPublicationDate: this.teaserBoxConfig.showPublicationDate,
                    showTitle: true,
                    size: TileSize.S,
                    mobileSize: this.gridConfig.mobileSize,
                    titleOnImage: true,
                    gradientColor: this.teaserBoxConfig.gradientColor,
                    showArticleTags: this.teaserBoxConfig.showArticleTags
                }
            }
        };
    }
    get tileConfig() {
        return {
            [TileSize.S]: {
                isPremium: this.teaserBoxConfig.isPremium,
                showArticleTags: this.teaserBoxConfig.showArticleTags,
                showAuthorAvatar: this.teaserBoxConfig.showAuthorAvatar,
                showAuthorName: this.teaserBoxConfig.showAuthorName,
                showCategory: false,
                showPublicationDate: this.teaserBoxConfig.showPublicationDate,
                showTitle: true,
                size: TileSize.S,
                mobileSize: this.gridConfig.mobileSize,
                titleOnImage: true,
                titleVersion: this.teaserBoxConfig.titleVersion,
                gradientColor: this.teaserBoxConfig.gradientColor
            },
            [TileSize.L]: {
                isPremium: this.teaserBoxConfig.isPremium,
                showArticleTags: this.teaserBoxConfig.showArticleTags,
                showAuthorAvatar: this.teaserBoxConfig.showAuthorAvatar,
                showAuthorName: this.teaserBoxConfig.showAuthorName,
                showCategory: this.teaserBoxConfig.showCategory,
                showPublicationDate: this.teaserBoxConfig.showPublicationDate,
                showTitle: true,
                size: TileSize.L,
                mobileSize: TileSizeMobile.TILE,
                titleOnImage: true,
                titleVersion: this.teaserBoxConfig.titleVersion,
                gradientColor: this.teaserBoxConfig.gradientColor
            },
            [TileSize.XL]: {
                isPremium: this.teaserBoxConfig.isPremium,
                showArticleTags: this.teaserBoxConfig.showArticleTags,
                showAuthorAvatar: this.teaserBoxConfig.showAuthorAvatar,
                showAuthorName: this.teaserBoxConfig.showAuthorName,
                showCategory: this.teaserBoxConfig.showCategory,
                showPublicationDate: this.teaserBoxConfig.showPublicationDate,
                showTitle: true,
                size: TileSize.XL,
                mobileSize: TileSizeMobile.TILE,
                titleOnImage: true,
                titleVersion: this.teaserBoxConfig.titleVersion,
                gradientColor: this.teaserBoxConfig.gradientColor
            }
        };
    }
};
TeaserBoxAddonsMixin = TeaserBoxAddonsMixin_1 = __decorate([
    Component({
        name: 'TeaserBoxAddonsMixin',
        components: { InnerGridWrapper, TextListWithAddons, Tile, ThumbnailsListWithAddons },
        template: `
    <InnerGridWrapper>
    <template v-if="items && items.length">
      <!-- Left column (3/4): Tiles and Addons -->
      <template v-for="item in tiles">
        <component :is="getComponentType(item)" v-bind="getComponentProps(item)" :key="item.key" />
      </template>
      <!-- Right column (1/4): TextList with Addons -->
      <template v-if="!isMobile && listComponent">
        <div :class="gridStyle">
          <component :is="listComponent.component" v-bind="getComponentProps(listComponent)"
                     :key="listComponent.key" />
        </div>
      </template>
    </template>
    </InnerGridWrapper>
  `
    })
], TeaserBoxAddonsMixin);
export { TeaserBoxAddonsMixin };
